import React, { useState, useEffect } from "react";
import { X, Clock, Users, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CONFIG } from "../config";

export default function RegistrationModal({ isOpen, onClose, onRegisterSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeInter] = useState(900); // 15 mins countdown

  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setTimeInter((prev) => (prev > 0 ? prev - 1 : 900));
    }, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      // Mock submit lead data to custom webhook URL if configured
      if (CONFIG.webhookUrl) {
        try {
          await fetch(CONFIG.webhookUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name,
              email,
              phone,
              creatorId: CONFIG.creatorId,
              funnelSlug: "ai-clarity-coach-launchpad",
              registeredAt: new Date().toISOString(),
            }),
          });
        } catch (err) {
          console.warn("Webhook submit failed, falling back gracefully: ", err);
        }
      }

      // Save to localStorage for custom greeting on later pages
      localStorage.setItem("tm_webinar_registered_user", JSON.stringify({ name, email }));

      toast.success("Seat Reserved Successfully!");
      onRegisterSuccess();
    } catch (err) {
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-fade-in"
      data-testid="webinar-registration-modal"
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-teal-100 overflow-hidden">
        {/* Reservation Countdown Banner */}
        <div className="bg-gradient-to-r from-teal-600 to-sky-600 text-white px-6 py-2.5 text-center text-xs sm:text-sm font-semibold flex items-center justify-center gap-2">
          <Clock className="w-4 h-4 animate-pulse" />
          <span>TICKET RESERVED FOR: <span className="font-mono bg-white/20 px-2 py-0.5 rounded text-white tracking-widest">{formatTime(timeLeft)}</span> MINS</span>
        </div>

        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-12 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close modal"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-6 sm:p-8 pt-8">
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold mb-2">
              <Sparkles className="w-3.5 h-3.5 text-teal-500" />
              100% FREE MASTERCLASS
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight">
              Claim Your Free Seat Now
            </h3>
            <p className="text-sm text-slate-500 mt-1.5">
              Enter your details below to secure your direct live access link.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name-input" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Full Name <span className="text-teal-600">*</span>
              </label>
              <input 
                id="name-input"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-slate-800 placeholder-slate-400 transition-all font-medium"
                data-testid="registration-form-name-input"
                required
              />
            </div>

            <div>
              <label htmlFor="email-input" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Email Address <span className="text-teal-600">*</span>
              </label>
              <input 
                id="email-input"
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-slate-800 placeholder-slate-400 transition-all font-medium"
                data-testid="registration-form-email-input"
                required
              />
            </div>

            <div>
              <label htmlFor="phone-input" className="block text-sm font-semibold text-slate-700 mb-1.5">
                WhatsApp / Phone Number <span className="text-slate-400 font-normal">(Optional for notifications)</span>
              </label>
              <input 
                id="phone-input"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-slate-800 placeholder-slate-400 transition-all font-medium"
                data-testid="registration-form-phone-input"
              />
            </div>

            <button 
              type="submit"
              disabled={loading}
              className="w-full mt-2 py-4 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-teal-100 flex items-center justify-center gap-2 text-base active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
              data-testid="registration-form-submit-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                "RESERVE MY FREE SEAT"
              )}
            </button>
          </form>

          {/* Social proof & privacy */}
          <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-500">
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <Users className="w-4 h-4 text-sky-500" />
              <span>🔥 28 people registering right now</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span>🔒 100% Free - Secure Registration</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
